/**
 * -------------- ANGULAR FRAMEWORK --------------------
 */
import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule, DecimalPipe, LowerCasePipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';
//  -------------------------------------------------- //

/**
 * -------------- THIRD-PARTY IMPORTS -------------------
 */
import { NgbModule } from '@ng-bootstrap/ng-bootstrap'; // todo: Only import what you need

import { HighchartsChartModule } from 'highcharts-angular';

import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ColorPickerModule } from 'ngx-color-picker';
import {
  AutocompleteModule,
  ButtonModule,
  DatepickerModule,
  DropdownModule,
  DynamicAccordionModule,
  DynamicTabsModule,
  RadioGroupModule
} from '@blocx/components';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { NgSelectModule } from '@ng-select/ng-select';
//  -------------------------------------------------- //

/**
 * ----------------- APP MODULES ------------------------
 */
import { ToastModule } from '@shared/modules/toast/toast.module';
//  -------------------------------------------------- //

/**
 * --------------- COMMON SERVICES ----------------------
 */
import { AuthGuard } from './guards/auth-guard.service';
import { ApiService } from './services/api.service';
import { HeartbeatService } from './services/heartbeat.service';
import { HelperUtilityService } from './services/helper-utility.service';
import { DataStoreService } from './services/dataStore.service';
import { ThemingService } from './services/theming.service';

import { ToastService } from '@shared/modules/toast/services/toast.service';

import { TicketsService } from '@shared/services/ticketing.service';
import { ChartService } from '@shared/services/charts.service';

import { FeatureService } from '@shared/services/feature.service';
import { FeatureGuard } from '@shared/guards/feature-guard.service';
import { GroupBasedAccessService } from '@shared/services/group-based-access-service';
//  -------------------------------------------------- //

/**
 * ------------- COMMON DIRECTIVES & PIPES --------------
 */
import { NgbdSortableHeaderDirective } from '@shared/directives/sortable.directive';
import { EqualValidator } from './directives/equal-validator.directive';
import { StickyPopoverDirective } from './directives/sticky-popover.directive';
import { NumericFieldDirective } from './directives/numeric-field.directive';
import { TruncatePipe } from './pipes/truncate.pipe';
import { ShowIfHasFeatureDirective } from './directives/show-if-has-feature.directive';
import { ShowIfUserIsInGroupDirective } from './directives/show-if-user-is-in-group.directive';
import { TypeofPipe } from '@shared/pipes/typeof.pipe';
//  -------------------------------------------------- //

/**
 * ------------ APPLICATION 'COMMON' COMPONENTS -----------
 * Add in declaration section. Do not add in entryComponents if its not a popup
 */
import { ComingModuleComponent } from './components/other-templates/coming-module/coming-module.component';
import { EditColumnsComponent } from './components/edit-columns/edit-columns.component';
import { TypeaheadFocusComponent } from './components/typeahead-focus/typeahead-focus.component';
import { LangBarComponent } from './components/lang-bar/lang-bar.component';
import { GoToTopComponent } from './components/go-to-top/go-to-top.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { ErrorPageComponent } from '@shared/components/error-page/error-page.component';
import { NotFoundComponent } from './components/notfound/notfound.component';
//  -------------------------------------------------- //

/**
 * ------- APPLICATION 'COMMON POPUP' COMPONENTS --------
 * Add in declarations and entryComponents sections
 * (Any module specific popups - move to specific module and add in declarations and entryComponents' sections)
 */
import { DurationComponent } from '@shared/components/duration/duration.component';
import { PopupBaseComponent } from '@shared/modules/popup/components/popup-templates/popup-base/popup-base.component';
import { MessagePopupComponent } from '@shared/modules/popup/components/popup-templates/message-popup/message-popup.component';
import { ConfirmationPopupComponent } from '@shared/modules/popup/components/popup-templates/confirmation-popup/confirmation-popup.component';
import { GlobalSearchComponent } from './components/global-search/global-search.component';
import { CustomThemePopupComponent } from './components/custom-theme-popup/custom-theme-popup.component';
//  -------------------------------------------------- //

/**
 * --------------- ROUTE RESOLVERS ----------------------
 * Add in providers section.
 *
 */
import { UserProfileResolver } from '@shared/resolvers/user-profile.resolver';
import { FeatureResolver } from '@shared/resolvers/feature.resolver';
import { AppRouteReuseStrategy } from './AppRouteReuseStrategy';
import { SysIdResolver } from '@shared/resolvers/sys-id.resolver';
//  -------------------------------------------------- //

/**
 * ------------------------ TODO -----------------------------------------
 * Anything that needs to be moved to appropriate sections above goes here
 *
 */
import { TicketingChartsComponent } from '../charts/ticketing-charts/ticketing-charts.component';

import { TicketingChartsService } from '../charts/ticketing-charts.service';

import { WebChatComponent } from '@shared/components/web-chat/web-chat.component';
import { FaqComponent } from './components/faq/faq.component';
import { NewIncidentComponent } from './components/new-incident/new-incident.component';

import { CustomizeChartViewComponent } from './components/customize-chart-view/customize-chart-view.component';
import { CustomizeChartsService } from './components/customize-chart-view/service/customize-chart.service';
import { CIAPChatbotComponent } from '@shared/components/ciap-chatbot/ciap-chatbot.component';
import { FormatDateTimePipe } from './pipes/format-date-time.pipe';
import { UnauthorisedAccessComponent } from './components/unauthorised-access/unauthorised-access.component';
import { CausecodeTabComponent } from '../apps/root-cause/component/causecode-tab/causecode-tab.component';
import { AiopsScenariosFiltersComponent } from './components/aiops-scenarios-filters/aiops-scenarios-filters.component';
import { SettingsResolver } from '@shared/resolvers/settings.resolver';
import { StriptagsPipe } from './pipes/striptags.pipe';
import { BayerChatbotComponent } from '@shared/components/bayer-chatbot/bayer-chatbot.component';
import { AiopsAlertsComponent } from './modules/chart-components/components/aiops/aiops-alerts/aiops-alerts.component';
import { AiopsAlertsFiltersComponent } from './components/aiops-alerts-filters/aiops-alerts-filters.component';
import { ScenarioAlertContainerComponent } from './components/scenario-alert-container/scenario-alert-container.component';
import { CommunicationService } from '@shared/services/communication.service';
import { TablePaginationComponent } from './components/table-pagination/table-pagination.component';
import { LangService } from '@shared/components/lang-bar/lang.service';
import { TranslateResolver } from '@shared/resolvers/translation.resolver';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { ReversePipe } from '@shared/pipes/reverse.pipe';
import { TrustHtmlPipe } from '@shared/pipes/trust-html.pipe';
import { AiopsIncidentsReportsComponent } from './components/aiops-incidents-reports/aiops-incidents-reports.component';
import { AiopsReportsDetailsPopupComponent } from './components/aiops-reports-details-popup/aiops-reports-details-popup.component';
import { AiopsReportsStatusFilterComponent } from './components/aiops-reports-status-filter/aiops-reports-status-filter.component';
import { AiopsReportsSearchColumnComponent } from './components/aiops-reports-search-column/aiops-reports-search-column.component';
import { AiopsReportsGenAiComponent } from './components/aiops-reports-genAi/aiops-reports-genAi.component';

//  -------------------------------------------------- //

// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http);
// }

// export function createTranslateLoader(http: HttpClient) {
//   return new TranslateHttpLoader(http, './assets/i18n/', '.json');
// }

@NgModule({
  declarations: [
    HeaderComponent,
    FooterComponent,
    LangBarComponent,
    GoToTopComponent,
    NgbdSortableHeaderDirective,
    EditColumnsComponent,
    EqualValidator,
    ComingModuleComponent,
    TicketingChartsComponent,
    TypeaheadFocusComponent,
    TruncatePipe,
    StickyPopoverDirective,
    NotFoundComponent,
    NumericFieldDirective,
    ErrorPageComponent,
    DurationComponent,
    PopupBaseComponent,
    MessagePopupComponent,
    ConfirmationPopupComponent,
    ShowIfHasFeatureDirective,
    GlobalSearchComponent,
    ShowIfUserIsInGroupDirective,
    WebChatComponent,
    FaqComponent,
    CustomThemePopupComponent,
    TypeofPipe,
    NewIncidentComponent,
    CustomizeChartViewComponent,
    CIAPChatbotComponent,
    FormatDateTimePipe,
    UnauthorisedAccessComponent,
    CausecodeTabComponent,
    AiopsScenariosFiltersComponent,
    StriptagsPipe,
    BayerChatbotComponent,
    ScenarioAlertContainerComponent,
    AiopsAlertsFiltersComponent,
    TablePaginationComponent,
    ReversePipe,
    TrustHtmlPipe,
    AiopsIncidentsReportsComponent,
    AiopsReportsDetailsPopupComponent,
    AiopsReportsStatusFilterComponent,
    AiopsReportsSearchColumnComponent,
    AiopsReportsGenAiComponent
  ],
  imports: [
    CommonModule,
    TranslateModule.forChild({}),
    ButtonModule,
    NgbModule,
    DynamicAccordionModule,
    DatepickerModule,
    RadioGroupModule,
    DropdownModule,
    AutocompleteModule,
    DynamicTabsModule,
    FormsModule,
    HighchartsChartModule,
    DragDropModule,
    RouterModule,
    NgMultiSelectDropDownModule.forRoot(),
    ToastModule,
    NgSelectModule,
    ColorPickerModule
  ],
  exports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    NgbModule,
    ButtonModule,
    TranslateModule,
    LangBarComponent,
    // TabsModule,
    DynamicAccordionModule,
    DynamicTabsModule,
    GoToTopComponent,
    DatepickerModule,
    RadioGroupModule,
    DropdownModule,
    AutocompleteModule,
    NgbdSortableHeaderDirective,
    EditColumnsComponent,
    EqualValidator,
    TicketingChartsComponent,
    HighchartsChartModule,
    DragDropModule,
    TypeaheadFocusComponent,
    TruncatePipe,
    StickyPopoverDirective,
    NotFoundComponent,
    NumericFieldDirective,
    NgMultiSelectDropDownModule,
    ErrorPageComponent,
    ToastModule,
    ShowIfHasFeatureDirective,
    NgSelectModule,
    GlobalSearchComponent,
    ShowIfUserIsInGroupDirective,
    FaqComponent,
    CustomThemePopupComponent,
    TypeofPipe,
    ColorPickerModule,
    NewIncidentComponent,
    CustomizeChartViewComponent,
    CIAPChatbotComponent,
    FormatDateTimePipe,
    UnauthorisedAccessComponent,
    CausecodeTabComponent,
    AiopsScenariosFiltersComponent,
    StriptagsPipe,
    BayerChatbotComponent,
    ScenarioAlertContainerComponent,
    AiopsAlertsFiltersComponent,
    TablePaginationComponent,
    ReversePipe,
    TrustHtmlPipe,
    AiopsIncidentsReportsComponent,
    AiopsReportsDetailsPopupComponent,
    AiopsReportsStatusFilterComponent,
    AiopsReportsSearchColumnComponent,
    AiopsReportsGenAiComponent
  ],
  providers: [
    LowerCasePipe,
    DecimalPipe,
    TicketingChartsService,
    DatePipe,
    {
      provide: RouteReuseStrategy,
      useClass: AppRouteReuseStrategy
    },
    AuthGuard,
    CookieService,
    ApiService,
    HeartbeatService,
    HelperUtilityService,
    ChartService,
    FeatureGuard,
    CustomizeChartsService,
    CommunicationService
  ],
  bootstrap: []
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        // only for singleton services
        // SomeService
        ToastService,
        ThemingService,
        DataStoreService,
        FeatureService,
        SysIdResolver,
        TicketsService,
        UserProfileResolver,
        FeatureResolver,
        SettingsResolver,
        GroupBasedAccessService,
        CommunicationService,
        TranslateResolver
      ]
    };
  }

  constructor() {
    // this.translate.currentLang = '';
    // this.translate.setDefaultLang(this.langService.getCurrentLanguage().languageCode);
    // this.translate.use(this.langService.getCurrentLanguage().languageCode);
  }
}
